/**
 * Visualização pública do Mapa Unik (link compartilhável, só leitura) — Motor Astral Atualizado.
 * Suporte a gestos touch (pinch-to-zoom, arrasto com inércia), auto-enquadramento responsivo
 * em mobile e desktop, Web Share API nativa e visual luxuoso Genesis Lux.
 */
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router";
import { Share2, Check, Maximize2, Minimize2, Eye, HelpCircle } from "lucide-react";
import { api } from "../lib/api";
import type { MapNode } from "../lib/mapModel";
import type { AstralMapData, AstralNode } from "../types/astral";
import { AstralCanvas } from "../components/astral/AstralCanvas";
import { BrandMark } from "../ui/BrandMark";
import { FullScreenLoader } from "../ui/ProtectedRoute";

interface PublicMapResponse {
  title: string;
  nodes?: MapNode[];
  data?: AstralMapData;
  layout?: string;
  theme?: string;
}

export function MapaPublico() {
  const { token } = useParams();
  const [mapResponse, setMapResponse] = useState<PublicMapResponse | null>(null);
  const [error, setError] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMobileHint, setShowMobileHint] = useState(true);

  // Auto-fade da dica mobile após 4.5 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowMobileHint(false);
    }, 4500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!token) {
      setError(true);
      return;
    }
    api
      .get<PublicMapResponse>(`/public/map/${token}`)
      .then(setMapResponse)
      .catch(() => setError(true));
  }, [token]);

  // Transforma nodes relacionais em AstralMapData se necessário
  const mapData = useMemo<AstralMapData>(() => {
    if (mapResponse?.data && mapResponse.data.nodes && mapResponse.data.rootId) {
      return mapResponse.data;
    }

    const defaultRootId = "root_public";
    if (!mapResponse?.nodes || mapResponse.nodes.length === 0) {
      return {
        rootId: defaultRootId,
        layout: "map",
        theme: "sellflux",
        viewport: { zoom: 1, panX: 0, panY: 0 },
        nodes: {
          [defaultRootId]: {
            id: defaultRootId,
            parentId: null,
            text: mapResponse?.title || "Mapa Mental",
            side: "right",
            order: 0,
            isCollapsed: false,
          },
        },
      };
    }

    const nodes = mapResponse.nodes;
    const root = nodes.find((n) => n.parentId === null) || nodes[0]!;
    const astralNodes: Record<string, AstralNode> = {};
    const directChildren = nodes.filter((n) => n.parentId === root.id);

    nodes.forEach((n) => {
      let side: "left" | "right" = "right";
      if (n.id !== root.id) {
        if (n.parentId === root.id) {
          const childIdx = directChildren.findIndex((c) => c.id === n.id);
          side = childIdx % 2 === 1 ? "left" : "right";
        } else {
          let curr = nodes.find((m) => m.id === n.parentId);
          while (curr && curr.parentId !== root.id && curr.parentId !== null) {
            curr = nodes.find((m) => m.id === curr?.parentId);
          }
          if (curr) {
            const cIdx = directChildren.findIndex((c) => c.id === curr?.id);
            side = cIdx % 2 === 1 ? "left" : "right";
          }
        }
      }

      astralNodes[n.id] = {
        id: n.id,
        parentId: n.parentId,
        text: n.label || "Tópico",
        note: n.body || undefined,
        color: n.color || undefined,
        side,
        order: 0,
        isCollapsed: false,
      };
    });

    return {
      rootId: root.id,
      layout: (mapResponse.layout as any) || "map",
      theme: (mapResponse.theme as any) || "sellflux",
      viewport: { zoom: 1, panX: 0, panY: 0 },
      nodes: astralNodes,
    };
  }, [mapResponse]);

  // Compartilhamento nativo no celular com fallback de cópia
  const handleShare = async () => {
    const url = window.location.href;
    const title = mapResponse?.title || "Mapa Estratégico Unik";

    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: `${title} | Unik`,
          text: `Confira o Mapa Estratégico "${title}":`,
          url,
        });
        return;
      } catch (err: any) {
        if (err.name === "AbortError") return;
      }
    }

    void navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => undefined);
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => undefined);
      }
      setIsFullscreen(false);
    }
  };

  if (error) {
    return (
      <div className="app-shell flex min-h-dvh flex-col items-center justify-center px-6 text-center bg-[#0b0505]">
        <BrandMark variant="creme" className="h-7" />
        <h1 className="type-h2 mt-4 text-text-cream">Mapa indisponível</h1>
        <p className="type-body mt-2 text-text-secondary">
          Este link não é válido, expirou ou o acesso público foi desativado.
        </p>
      </div>
    );
  }

  if (mapResponse === null) return <FullScreenLoader />;

  return (
    <div className="flex h-dvh w-screen flex-col overflow-hidden bg-[#0a0505] select-none">
      {/* Header Fino e Elegante Genesis Lux */}
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border-default/60 bg-[#160a0a]/95 px-4 sm:px-6 backdrop-blur-md z-20">
        <div className="flex items-center gap-3 min-w-0">
          <BrandMark variant="creme" className="h-5 shrink-0" />
          <div className="h-4 w-px bg-white/10 hidden sm:block" />
          <h1 className="text-xs sm:text-sm font-semibold tracking-wide text-text-cream truncate">
            {mapResponse.title}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-gold/10 border border-gold/25 px-2.5 py-1 text-[11px] font-medium text-gold">
            <Eye size={12} />
            <span>Somente Leitura</span>
          </span>

          <button
            type="button"
            onClick={handleShare}
            className="flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-base px-2.5 py-1.5 text-xs text-text-secondary hover:text-white hover:border-gold transition-colors"
            title="Compartilhar mapa"
          >
            {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Share2 size={14} />}
            <span className="hidden sm:inline">{copiedLink ? "Link Copiado!" : "Compartilhar"}</span>
          </button>

          <button
            type="button"
            onClick={toggleFullscreen}
            className="p-1.5 rounded-lg border border-border-default bg-surface-base text-text-secondary hover:text-white hover:border-gold transition-colors"
            title={isFullscreen ? "Sair da Tela Cheia" : "Tela Cheia"}
          >
            {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
        </div>
      </header>

      {/* Dica Mobile Inicial (Auto-Fade) */}
      {showMobileHint && (
        <aside
          role="status"
          aria-label="Instruções de navegação touch"
          className="md:hidden absolute top-16 left-4 right-4 z-30 flex items-center justify-between gap-2 rounded-xl border border-gold/40 bg-[#1e0d0d]/95 px-3.5 py-2.5 text-xs text-text-cream shadow-2xl backdrop-blur-md animate-fade-in"
        >
          <div className="flex items-center gap-2">
            <HelpCircle size={15} className="text-gold shrink-0" />
            <span className="text-[11px]">
              Arraste para navegar • Pinça com dois dedos para zoom • Toque duplo para enquadrar
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowMobileHint(false)}
            aria-label="Fechar dica"
            className="text-text-tertiary hover:text-white text-xs px-1"
          >
            ✕
          </button>
        </aside>
      )}

      {/* Canvas Astral com Gestos Touch Nativos e AutoFit */}
      <main className="relative flex-1 w-full h-full overflow-hidden">
        <AstralCanvas
          mapData={mapData}
          readOnly={true}
          autoFitOnMount={true}
        />
      </main>
    </div>
  );
}
